import { io } from 'socket.io-client';

let socket = null;
let refreshTokenFn = null;
let logoutFn = null;
let refreshPromise = null; // Concurrency guard to prevent multiple simultaneous refresh attempts
let isInitialized = false; // Track if initSocket has been called
let visibilityHandler = null; // Track visibility change listener for cleanup

/**
 * Initialize the socket manager with auth callbacks
 * MUST be called before getSocket() will create a connection
 * Call this once from AuthContext after refreshToken and logout are defined
 */
export const initSocket = ({ refreshToken, logout }) => {
    refreshTokenFn = refreshToken;
    logoutFn = logout;
    isInitialized = true;

    // Set up visibility change listener to reconnect when tab becomes active
    if (!visibilityHandler) {
        visibilityHandler = handleVisibilityChange;
        document.addEventListener('visibilitychange', visibilityHandler);
    }
};

/**
 * Handle tab visibility changes - refresh token and reconnect socket when tab becomes visible
 */
const handleVisibilityChange = async () => {
    if (document.visibilityState !== 'visible') return;
    if (!socket || !refreshTokenFn) return;

    // If socket is already connected, nothing to do
    if (socket.connected) return;

    console.log('[Socket] Tab became visible, reconnecting...');

    try {
        const newToken = await refreshTokenFn();
        if (newToken) {
            socket.auth = { token: newToken };
            socket.connect();
        }
    } catch (err) {
        console.error('[Socket] Failed to refresh token on tab focus:', err);
        // Don't logout here - the HTTP layer may still work fine.
        // The user will see a connection error in the UI if they try to use socket features.
    }
};

/**
 * Get the shared socket instance
 * Returns null if initSocket hasn't been called yet (prevents race condition)
 * Creates the socket on first call after init, returns existing instance on subsequent calls
 */
export const getSocket = () => {
    // Don't create socket until auth callbacks are initialized
    // This prevents race condition where child components call getSocket
    // before AuthContext's useEffect runs initSocket
    if (!isInitialized) {
        console.warn('[Socket] getSocket called before initSocket - deferring connection');
        return null;
    }

    // Return existing socket (connected or not) - only create once
    if (socket) return socket;

    // Create socket ONCE with current access token
    const token = localStorage.getItem('accesstoken');

    socket = io(import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001', {
        withCredentials: true,
        transports: ['websocket', 'polling'],
        auth: { token },
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 30000
    });

    // Attach listeners ONCE when socket is created
    socket.on('connect', () => {
        console.log('[Socket] Connected:', socket.id);
    });

    socket.on('disconnect', (reason) => {
        console.log('[Socket] Disconnected:', reason);
    });

    socket.on('connect_error', async (error) => {
        const code = error.data?.code;

        // Only handle auth errors - let other errors use normal reconnection
        if (code !== 'AUTH_REQUIRED' && code !== 'AUTH_INVALID_TOKEN') {
            console.error('[Socket] Connection error:', error.message);
            return;
        }

        console.log('[Socket] Auth failed, attempting token refresh...');

        // Safety check - callbacks should always exist now due to isInitialized gate
        if (!refreshTokenFn) {
            console.error('[Socket] No refresh function available - this should not happen');
            return;
        }

        // Prevent concurrent refresh storms - if already refreshing, wait for that promise
        if (!refreshPromise) {
            refreshPromise = (async () => {
                try {
                    // Pause auto-reconnection while we refresh the token
                    // to prevent it from burning attempts with the stale token
                    socket.io.reconnection(false);

                    const newToken = await refreshTokenFn();
                    if (newToken) {
                        socket.auth = { token: newToken };
                        // Re-enable auto-reconnection before connecting
                        socket.io.reconnection(true);
                        socket.connect();
                        return true;
                    }
                    throw new Error('No token from refresh');
                } catch (err) {
                    console.error('[Socket] Token refresh failed:', err.message);
                    // If refreshToken cleared auth state (session truly expired),
                    // stop reconnecting — the user will be redirected to login.
                    // Otherwise re-enable auto-reconnection for transient errors.
                    const isLoggedOut = !localStorage.getItem('accesstoken');
                    if (isLoggedOut) {
                        socket.io.reconnection(false);
                    } else {
                        socket.io.reconnection(true);
                    }
                    return false;
                } finally {
                    refreshPromise = null;
                }
            })();
        }

        await refreshPromise;
    });

    return socket;
};

/**
 * Ensure the socket is connected, attempting reconnection if needed.
 * Refreshes the token and reconnects if the socket is disconnected.
 * Returns true if connected, false if reconnection failed.
 */
export const ensureConnected = async () => {
    const s = getSocket();
    if (!s) return false;
    if (s.connected) return true;

    if (!refreshTokenFn) return false;

    try {
        const newToken = await refreshTokenFn();
        if (!newToken) return false;

        s.auth = { token: newToken };
        s.connect();

        // Wait for connection (up to 5 seconds)
        return await new Promise((resolve) => {
            const timeout = setTimeout(() => resolve(false), 5000);

            const onConnect = () => {
                clearTimeout(timeout);
                s.off('connect', onConnect);
                s.off('connect_error', onError);
                resolve(true);
            };

            const onError = () => {
                clearTimeout(timeout);
                s.off('connect', onConnect);
                s.off('connect_error', onError);
                resolve(false);
            };

            s.once('connect', onConnect);
            s.once('connect_error', onError);
        });
    } catch (err) {
        console.error('[Socket] ensureConnected failed:', err);
        return false;
    }
};

/**
 * Update the socket auth token
 * Call this when the access token is refreshed via HTTP to keep socket in sync
 */
export const updateSocketAuth = (newToken) => {
    if (socket) {
        socket.auth = { token: newToken };
        // If disconnected, this will be used on next connect attempt
        // If connected, will be used on next reconnect
    }
};

/**
 * Disconnect and clean up the socket
 * Call this on logout
 */
export const disconnectSocket = () => {
    if (socket) {
        socket.disconnect();
        socket = null;
    }
    refreshPromise = null;
    isInitialized = false; // Reset on logout so initSocket must be called again

    // Clean up visibility listener
    if (visibilityHandler) {
        document.removeEventListener('visibilitychange', visibilityHandler);
        visibilityHandler = null;
    }
};
